import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
const _excluded = ["children", "visibleItemsCount", "expandButtonText", "collapseButtonText", "initialExpanded", "inline", "onExpandedChange", "dataTestId"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import * as React from 'react';
import { ToggleButton } from '../ToggleButton';
import { useCollapsedHeight, useExpander, useTransitionDurationVar } from '../hooks';
import { clsx } from 'clsx';
import styles from './ExpanderList.module.css';
import varsStyles from '../styles/vars.module.css';
import { useEventCallback } from '@cian/react-utils';
import { DEFAULT_COLLAPSE_BUTTON_TEXT, DEFAULT_EXPAND_BUTTON_TEXT, DEFAULT_LIST_TEST_ID, EXPANDED_HEIGHT_DEFAULT, INLINE_TRANSITION_DURATION, TRANSITION_DURATION } from '../constants';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const ExpanderListComponent = (_ref, ref) => {
  let {
      children,
      visibleItemsCount,
      expandButtonText: expandButtonTextPrefix = DEFAULT_EXPAND_BUTTON_TEXT,
      collapseButtonText = DEFAULT_COLLAPSE_BUTTON_TEXT,
      initialExpanded = false,
      inline = false,
      onExpandedChange,
      dataTestId = DEFAULT_LIST_TEST_ID
    } = _ref,
    restProps = _objectWithoutProperties(_ref, _excluded);
  const contentRef = React.useRef(null);
  const items = React.Children.toArray(children);
  const [visibleItems, setVisibleItems] = React.useState(initialExpanded ? items : items.slice(0, visibleItemsCount));
  const handleExpandedChange = useEventCallback(expanded => {
    if (expanded) {
      setVisibleItems(items);
    } else {
      const delay = inline ? INLINE_TRANSITION_DURATION : TRANSITION_DURATION;
      setTimeout(() => {
        setVisibleItems(items.slice(0, visibleItemsCount));
      }, delay);
    }
    if (onExpandedChange) {
      onExpandedChange(expanded);
    }
  });
  const {
    expanded,
    onToggle,
    size
  } = useExpander(_objectSpread({
    onExpandedChange: handleExpandedChange
  }, restProps));
  const collapsedHeight = useCollapsedHeight(contentRef);
  const [expandedHeight, setExpandedHeight] = React.useState(initialExpanded ? EXPANDED_HEIGHT_DEFAULT : undefined);
  React.useEffect(() => {
    const contentElement = contentRef.current;
    if (visibleItems.length === items.length) {
      setExpandedHeight(contentElement.scrollHeight);
    }
  }, [visibleItems, items]);
  useTransitionDurationVar(contentRef);
  const expandButtonText = React.useMemo(() => {
    const itemsCount = items.length - visibleItemsCount;
    return "".concat(expandButtonTextPrefix, " ").concat(itemsCount);
  }, [expandButtonTextPrefix, items, visibleItemsCount]);
  const height = expanded && expandedHeight ? expandedHeight : collapsedHeight;
  return /*#__PURE__*/_jsxs("div", {
    className: varsStyles['expander'],
    ref: ref,
    "data-testid": dataTestId,
    children: [/*#__PURE__*/_jsx("ul", {
      className: clsx(styles['content'], inline && styles['content-inline']),
      ref: contentRef,
      style: {
        maxHeight: height,
        height: inline ? height : undefined
      },
      children: visibleItems.map((item, index) => /*#__PURE__*/_jsx("li", {
        children: item
      }, index))
    }), items.length > visibleItemsCount && /*#__PURE__*/_jsx("div", {
      className: styles['toggle-container'],
      children: /*#__PURE__*/_jsx(ToggleButton, {
        expanded: expanded,
        onToggle: onToggle,
        size: size,
        expandButtonText: expandButtonText,
        collapseButtonText: collapseButtonText
      })
    })]
  });
};

/**
 * Вариация компонента только для списка.
 * <br />
 * Принимает элементы списка и тогглит их на основании visibleItemsCount. Есть вариация рендера инлайново.
 *
 * [Ссылка на макет](https://www.figma.com/design/il7C7QByCaIpFdBZcyhEGv/%F0%9F%8C%90-Expander)
 */
export const ExpanderList = /*#__PURE__*/React.forwardRef(ExpanderListComponent);